import React, { createContext, useContext, useEffect, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { archivedActions, authActions } from '../_store';
import { useDispatch } from 'react-redux';
import { getStoredValue, setStoredValue } from '../_helpers/storagehelper';

const SignalRContext = createContext(null);

export const SignalRProvider = ({ children }) => {
  const [connection, setConnection] = useState(null);
  const publicUserId = getStoredValue("userID");
  const dispatch = useDispatch();
  const baseUrl = `${process.env.REACT_APP_API_URL}`;

  useEffect(() => {

    function capitalizeKeys(obj) {
      const newObj = {};
      for (const key in obj) {
        if (Object.hasOwn(obj, key)) {
          const capitalKey = key.charAt(0).toUpperCase() + key.slice(1);
          newObj[capitalKey] = obj[key];
        }
      }
      return newObj;
    }

    const startConnection = async () => {
      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${baseUrl}/chatHub`, { withCredentials: true })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Information)
        .build();

      /* ============================
         ✔ REGISTER EVENT LISTENERS
         ============================ */
      newConnection.on("ReceiveMessage", (user, message, type) => {
        switch (type) {
          case "Send_Msg":
             message = {
              ...message?.msgCommunicationSentWebModel,
              MessageStatus: message?.isCOD ? "C.O.D Pending" : "",
              IsSenderMsgDelete: false,
              IsCOD: message?.isCOD,
              MessageSentDate: message?.messageSentDate,
              MessageID: message?.messageID,
              InmateID: message?.msgCommunicationSentWebModel?.inmatesId,
              inmatesId: message?.msgCommunicationSentWebModel?.inmatesId,
              messageBody: message?.isCOD
                ? "Once you purchase credits, you can approve these messages"
                : message?.msgCommunicationSentWebModel?.messageBody,
            };
            break;

          case "Rejected_Msg":
            message = { ...message?.updateCODMessage, messageSentDate: message?.messageSentDate };
            break;

          case "Approved_Msg":
            message = {
              ...message?.updateCODMessage,
              MessageBodyText: message?.updateCODMessage?.messagetxt,
              messageBody: message?.updateCODMessage?.messagetxt,
            };
            break;
        }

        const capitalizedData = capitalizeKeys(message);

        dispatch(authActions.updateSignalRData({
          user,
          message: type === "Read_Ecard" ? message : capitalizedData,
          type
        }));

        if (publicUserId) {
          dispatch(archivedActions.PULoginUnreadNotificationCount({ publicUserId }));
        }
      });

      /* ============================
         ✔ AUTO RECONNECT EVENTS
         ============================ */

      newConnection.onreconnecting(() => {
        console.log("SignalR reconnecting...");
      });

      newConnection.onreconnected(async () => {
        console.log("SignalR reconnected!");

        // 🔥 NO .start() here.
        // Only re-register user.
        if (publicUserId) {
          const sid = await newConnection.invoke("RegisterUser", `PU_${publicUserId}`);

          const old = getStoredValue(`signalRId_${publicUserId}`);
          if (old && old !== sid) {
            newConnection.invoke("RemoveUser", old);
          }
          setStoredValue(`signalRId_${publicUserId}`, sid);
        }
      });

      newConnection.onclose(() => {
        console.log("SignalR connection closed.");
        // ❌ do NOT call .start() here
      });

      try {
        /* ============================
           ✔ START ONLY ONCE
         ============================ */
        await newConnection.start();
        console.log("SignalR connected!");

        if (publicUserId) {
          const sid = await newConnection.invoke("RegisterUser", `PU_${publicUserId}`);
          setStoredValue(`signalRId_${publicUserId}`, sid);
        }

        setConnection(newConnection);
        dispatch(authActions.updateconnectionData(newConnection));

      } catch (err) {
        console.log("❌ SignalR start error:", err);
      }
    };

    startConnection();

    return () => {
      if (connection) {
        connection.stop();
      }
    };
  }, []);

  return (
    <SignalRContext.Provider value={connection}>
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalR = () => useContext(SignalRContext);
